import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, defer, EMPTY, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mapTo, mergeMap, reduce, tap, toArray } from 'rxjs/operators';
import { ArrayHelper } from '../../../helpers/arrayHelper';
import { FileHelper } from '../../../helpers/fileHelper';
import { GuidHelper } from '../../../helpers/guidHelper';
import { IdHelper } from '../../../helpers/idHelper';
import { StringHelper } from '../../../helpers/stringHelper';
import { EApplicationEventType } from '../../../model/application/EApplicationEventType';
import { ConfigData } from '../../../model/config/ConfigData';
import { EPrefix } from '../../../model/EPrefix';
import { EDatabaseRole } from '../../../model/store/EDatabaseRole';
import { EStoreFlag } from '../../../model/store/EStoreFlag';
import { IDataSource } from '../../../model/store/IDataSource';
import { IStoreDataResponse } from '../../../model/store/IStoreDataResponse';
import { IStoreDocument } from '../../../model/store/IStoreDocument';
import { ApplicationService } from '../../../services/application.service';
import { Store } from '../../../services/store.service';
import { FilesystemService } from '../../filesystem/services/filesystem.service';
import { ELogActionId } from '../../logger/models/ELogActionId';
import { LoggerService } from '../../logger/services/logger.service';
import { DmsFileHelper } from '../helpers/dmsFileHelper';
import { DmsFile } from '../model/DmsFile';
import { EDmsAction } from '../model/EDmsAction';
import { EDmsFlag } from '../model/EDmsFlag';
import { IDms } from '../model/IDms';
import { IDmsData } from '../model/IDmsData';
import { IDmsEvent } from '../model/IDmsEvent';
import { IDmsMeta } from '../model/IDmsMeta';
import { LocalDmsService } from './localDms.service';
import { RemoteDmsService } from './remoteDms.service';

@Injectable({ providedIn: "root" })
export class SyncDmsService implements IDms {

	//#region FIELDS

	/** Identifiant du service pour les logs. */
	private static readonly C_LOG_ID = "SDMS.S::";

	/** Sujet pour l'envoi d'événements de dms. */
	private readonly moDmsEventSubject = new Subject<IDmsEvent>();

	/** Identifiant de la base de données du DMS. */
	private C_DMS_DATABASE_ID: string;

	private readonly C_LOG_ID = "SDMS.S::";

	//#endregion

	//#region METHODS

	constructor(
		/** Service des requêtes en base de données. */
		private readonly isvcStore: Store,
		/** Service de gestion des fichiers DMS en local. */
		private readonly isvcLocalDms: LocalDmsService,
		/** Service de gestion des fichiers DMS côté serveur. */
		private readonly isvcRemoteDms: RemoteDmsService,
		private readonly isvcLogger: LoggerService,
		private readonly isvcFilesystem: FilesystemService,
		psvcApplication: ApplicationService
	) {
		this.init(psvcApplication);
	}

	/** Permet de savoir si le code erreur http correspond à une erreur qui sera permanente.
	 * @param poError Erreur reçue.
	 */
	public isPermanentError(poError: HttpErrorResponse): boolean {
		// "not found" ou "bad request" ou une erreur inconnue.
		return poError.status === 400 || poError.status === 404;
	}

	/** Initialisation du service en lançant les requêtes en attente après que le service du DMS local soit correctement initialisé.
	 * @param psvcApplication Service de gestion de l'application.
	 */
	private init(psvcApplication: ApplicationService): void {
		psvcApplication.waitForFlag(EStoreFlag.DBInitialized, true)
			.pipe(tap(_ => this.C_DMS_DATABASE_ID = ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.dmsStorage))))
			.subscribe();

		psvcApplication.waitForFlag(EDmsFlag.DmsReady, true)
			.pipe(tap(_ => this.requestExecPendings()))
			.subscribe();
	}

	/** Supprime un fichier en local dans le DMS et tente de le supprimer du serveur ou non.
	 * @param psId Identifiant du fichier à supprimer.
	 */
	public delete(psId: string): Observable<boolean> {
		const lsRemoveDocumentId: string = IdHelper.buildId(EPrefix.dms, psId);

		return this.isvcLocalDms.delete(lsRemoveDocumentId)
			.pipe(
				mergeMap(_ => this.putDeleteRequest(lsRemoveDocumentId)),
				tap(_ => this.requestExecPendingDelete(lsRemoveDocumentId)),
				mergeMap(_ => this.waitFile(lsRemoveDocumentId)),
				mapTo(true)
			);
	}

	/** Exécute toutes les suppressions de fichiers.
	 * @param paPendings Tous les documents en attente.
	 * @param pbHasToContinueIfFail `true` si on veut inclure les fichiers tombés en erreur (et permet de continuer les traitements),
	 * `false` sinon (`false` par défaut, tous les traitements s'arrêtent à la moindre erreur).
	 * @returns Le tableau des identifiants des fichiers meta supprimés.
	 */
	private execDeletes(paPendings: IStoreDocument[], pbHasToContinueIfFail: boolean = false): Observable<Array<string>> {
		const laPendingDeleteGuids: string[] = this.extractGuidsFromPendings(paPendings, /del_(.+)/);
		const lfResolve$: (poError) => Observable<boolean | never> = (poError) => pbHasToContinueIfFail ? of(false) : throwError(poError);

		if (ArrayHelper.hasElements(laPendingDeleteGuids)) {
			return defer(() => {
				console.debug(`${SyncDmsService.C_LOG_ID}Deleting documents ${laPendingDeleteGuids.join(", ")}...`);
				return from(laPendingDeleteGuids);
			})
				.pipe(
					mergeMap((psId: string) => {
						return this.isvcRemoteDms.delete(psId)
							.pipe(
								tap(
									_ => console.debug(`${SyncDmsService.C_LOG_ID}Deleting document ${psId} SUCCESSED !`),
									_ => console.debug(`${SyncDmsService.C_LOG_ID}Deleting document ${psId} FAILED !`)
								),
								catchError(poError => this.isPermanentError(poError) ?
									this.removeDeleteRequest(psId).pipe(mergeMap(_ => lfResolve$(poError))) : lfResolve$(poError)
								),
								map((pbResult: boolean) => pbResult ? psId : undefined)
							);
					}),
					mergeMap((psId?: string) => StringHelper.isBlank(psId) ? of(null) : this.removeDeleteRequest(psId).pipe(mapTo(psId))),
					toArray()
				);
		}
		else
			return EMPTY;
	}

	/** Exécute tous les téléchargements de fichiers.
	 * @param paPendings Tous les documents en attente.
	 * @param pbHasToContinueIfFail `true` si on veut inclure les fichiers tombés en erreur (et permet de continuer les traitements),
	 * `false` sinon (`false` par défaut, tous les traitements s'arrêtent à la moindre erreur).
	 * @returns Le tableau des identifiants des fichiers meta récupérés.
	 */
	private execDownloads(paPendings: IStoreDocument[], pbHasToContinueIfFail: boolean = false): Observable<Array<string>> {
		const laPendingDownloadGuids: string[] = this.extractGuidsFromPendings(paPendings, /dl_(.+)/);

		if (ArrayHelper.hasElements(laPendingDownloadGuids)) {
			return defer(() => {
				console.debug(`${SyncDmsService.C_LOG_ID}Downloading documents ${laPendingDownloadGuids.join(", ")}...`);
				return from(laPendingDownloadGuids);
			})
				.pipe(
					mergeMap((psGuid: string) => {
						return this.downloadFile(psGuid)
							.pipe(
								tap(
									_ => console.debug(`${SyncDmsService.C_LOG_ID}SDMS.S::Downloading document ${psGuid} SUCCESSED !`),
									_ => console.debug(`${SyncDmsService.C_LOG_ID}SDMS.S::Downloading document ${psGuid} FAILED !`)
								),
								catchError(poError => pbHasToContinueIfFail ? of({} as IDmsMeta) : throwError(poError)),
								map((poMeta?: IDmsMeta) => poMeta?._id ?? null)
							);
					}),
					toArray()
				);
		}
		else
			return EMPTY;
	}

	/** Télécharge le fichier et ses méta.
	 * @param psGuid Guid du fichier à télécharger.
	 * @returns Les méta-données du fichier téléchargé, null si le fichier était déjà présent localement et qu'il n'a pas été re-téléchargé.
	 */
	private downloadFile(psGuid: string): Observable<IDmsMeta> {
		const lsId: string = IdHelper.buildId(EPrefix.dms, psGuid);

		console.debug(`${SyncDmsService.C_LOG_ID}Checking document availability in ${ConfigData.environment.dms.applicationPath} for document ${lsId}...`);

		return this.isDocumentAvailable(lsId)
			.pipe(
				mergeMap((pbExists: boolean) => {
					if (!pbExists) {
						console.debug(`${SyncDmsService.C_LOG_ID}Downloading file ${psGuid}...`);

						return this.isvcRemoteDms.get(psGuid)
							.pipe(
								catchError(poError => this.onGetError(poError, IdHelper.buildId(EPrefix.dms, psGuid))),
								mergeMap((poResult: IDmsData) => this.isvcLocalDms.save(poResult.file, poResult.meta))
							);
					}
					else {
						console.warn(`${SyncDmsService.C_LOG_ID}Pending download ignored because document ${lsId} already exists locally.`);

						return of(null);
					}
				}),
				mergeMap((poResultMeta: IDmsMeta) => this.removeDownloadRequest(psGuid).pipe(mapTo(poResultMeta)))
			);
	}

	private isDocumentAvailable(psDocumentId: string): Observable<boolean> {
		return defer(() => this.isvcFilesystem.existsAsync(`${ConfigData.environment.dms.applicationPath}${psDocumentId}`));
	}

	/** Exécute la regex sur chaque élément de la liste et retourne toutes les correspondances.
	 * @param paResults Jeu de résultat.
	 * @param loRegex Regex à exécuter.
	 */
	private extractGuidsFromPendings(paResults: IStoreDocument[], loRegex: RegExp): string[] {
		const laPendingGuids: string[] = [];

		paResults.forEach((poResult: IStoreDocument) => {
			const loRegexResult: RegExpExecArray = loRegex.exec(poResult._id);
			const lsLastElement: string = loRegexResult ? ArrayHelper.getLastElement(loRegexResult) : null;
			if (!StringHelper.isBlank(lsLastElement))
				laPendingGuids.push(lsLastElement);
		});
		return laPendingGuids;
	}

	/** Exécute toutes les tâches de synchro.
	 * @param paResults Résultats de la requête sur la base (toutes les tâches en attente).
	 * @param pbHasToContinueIfFail `true` si on veut inclure les fichiers tombés en erreur (et permet de continuer les traitements),
	 * `false` sinon (`false` par défaut, tous les traitements s'arrêtent à la moindre erreur).
	 * @returns Le tableau des identifiants des fichiers traités (téléchargés, téléversés, supprimés), si `pbHasToReturnFailures` est à `true`,
	 * chaque fichier qui est tombé en erreur renverra la valeur `null`.
	 */
	private execSync(paResults: IStoreDocument[], pbHasToContinueIfFail: boolean): Observable<Array<string>> {
		//NB : concat est utilisé en contournement de problème avec File.checkDir qui semble ne pas retourner de réponse lors d'appels concurrents.
		return concat(
			this.execDownloads(paResults, pbHasToContinueIfFail).pipe(finalize(() => console.debug(`${SyncDmsService.C_LOG_ID}Downloads finished.`))),
			this.execUploads(paResults, pbHasToContinueIfFail).pipe(finalize(() => console.debug(`${SyncDmsService.C_LOG_ID}Uploads finished.`))),
			this.execDeletes(paResults, pbHasToContinueIfFail).pipe(finalize(() => console.debug(`${SyncDmsService.C_LOG_ID}Deletes finished.`)))
		)
			.pipe(reduce((paIds: string[], paResult: string[]) => [...paIds, ...paResult], []));
	}

	/** Exécute tous les téléversements de fichiers.
	 * @param paPendings Tous les doucuments en attente.
	 * @param pbHasToContinueIfFail `true` si on veut inclure les fichiers tombés en erreur (et permet de continuer les traitements),
	 * `false` sinon (`false` par défaut, tous les traitements s'arrêtent à la moindre erreur).
	 * @returns Le tableau des identifiants des fichiers meta téléversés.
	 */
	private execUploads(paPendings: IStoreDocument[], pbHasToContinueIfFail: boolean = false): Observable<Array<string>> {
		const laPendingUploadGuids: string[] = this.extractGuidsFromPendings(paPendings, /ul_(.+)/);
		const lfResolve$: (poError) => Observable<undefined | never> = (poError) => pbHasToContinueIfFail ? of(undefined) : throwError(poError);

		if (ArrayHelper.hasElements(laPendingUploadGuids)) {
			return defer(() => {
				console.debug(`${SyncDmsService.C_LOG_ID}Uploading documents ${laPendingUploadGuids.join(", ")}...`);
				return from(laPendingUploadGuids);
			})
				.pipe(
					mergeMap((psGuid: string) => this.isvcLocalDms.get(IdHelper.buildId(EPrefix.dms, psGuid))
						.pipe(
							mergeMap((poResult: IDmsData) => this.isvcRemoteDms.save(poResult.file, poResult.meta).pipe(mapTo(psGuid))),
							tap(_ => console.debug(`${SyncDmsService.C_LOG_ID}Uploading document ${psGuid} SUCCESSED`)),
							catchError(poError => {
								console.debug(`${SyncDmsService.C_LOG_ID}Uploading document ${psGuid} FAILED`);
								return this.isPermanentError(poError) ?
									this.removeUploadRequest(psGuid).pipe(mergeMap(_ => lfResolve$(poError))) : lfResolve$(poError);
							})
						)),
					mergeMap((psGuid?: string) => StringHelper.isBlank(psGuid) ? of(null) : this.removeUploadRequest(psGuid).pipe(mapTo(psGuid))),
					toArray()
				);
		}
		else
			return EMPTY;
	}

	/**
	 * Supprime un document de la boîte d'envoi.
	 * ATTENTION Le document local sera définitivement détruit avant d'avoir été transmis, et donc perdu !
	 */
	public cancelDocumentUpload(psId: string): Observable<string> {
		if (!StringHelper.isBlank(psId)) {
			return defer(() => {
				console.debug(`${this.C_LOG_ID}Cancelling document ${psId} upload...`);
				return this.getPendingDoc(EPrefix.pendingUpload, psId);
			})
				.pipe(
					mergeMap(_ => this.removeUploadRequest(psId)),
					mergeMap(_ => this.isvcLocalDms.delete(IdHelper.buildId(EPrefix.dms, psId))),
					tap(() => this.isvcLogger.action(this.C_LOG_ID, `Pending document ${psId} upload was cancelled.`, ELogActionId.dmsDocUploadCancelled, { id: psId })),
					mapTo(psId),
					catchError(poError => {
						console.error(`SDMS.S::Document ${psId} upload cancellation failed.`, poError);

						return throwError(poError);
					})
				);
		}
		else
			return EMPTY;
	}

	/** @override */
	public get(psGuid: string): Observable<IDmsData> {
		const lsId: string = IdHelper.buildId(EPrefix.dms, psGuid);
		const loDataSource: IDataSource = {
			databaseId: this.C_DMS_DATABASE_ID,
			viewParams: {
				key: lsId,
				include_docs: true
			}
		};

		return this.isvcStore.getOne<IDmsMeta>(loDataSource, false)
			.pipe(
				mergeMap((poLocalMeta?: IDmsMeta) => {
					if (poLocalMeta && poLocalMeta.saved !== false) // Si le flag `saved` n'est pas marqué à false spécifiquement, alors c'est considéré comme true.
						return this.getFile(poLocalMeta, psGuid);
					else {
						return this.putDownloadRequest(psGuid)
							.pipe(
								tap(_ => this.requestExecPendingDownload(lsId)),
								mergeMap(_ => this.waitFile(lsId))
							);
					}
				})
			);
	}

	/** Récupère un fichier.
	 * @param poMeta Méthadonnées du fichier à récupérer.
	 * @param psGuid Guid du fichier à récupérer.
	 */
	private getFile(poMeta: IDmsMeta, psGuid: string): Observable<IDmsData> {
		const lsFileExtension: string | undefined = FileHelper.getFileExtensionFromFileName(poMeta.name);
		const lsFileName: string = lsFileExtension ? `${poMeta._id}.${lsFileExtension}` : poMeta._id;
		const lsPath = `${ConfigData.environment.dms.applicationPath}${poMeta._id}/${lsFileName}`;

		return defer(() => this.isvcFilesystem.existsAsync(lsPath))
			.pipe(
				mergeMap((pbFileExists: boolean) => {
					if (pbFileExists)
						return this.isvcFilesystem.getFileAsync(lsPath);
					else {
						return this.putDownloadRequest(psGuid)
							.pipe(
								tap(_ => this.requestExecPendingDownload(poMeta._id)),
								mergeMap(_ => this.waitFile(poMeta._id)),
								map((poResult: IDmsData) => poResult.file.File)
							);
					}
				}),
				map((poFile: string | Blob) => {
					if (!poFile)
						throw new Error("Le fichier récupéré est nul");
					else {
						return {
							file: new DmsFile(poFile, poMeta),
							meta: poMeta
						};
					}
				})
			);
	}

	private raiseDmsEvent(poEvent: IDmsEvent): void {
		this.moDmsEventSubject.next(poEvent);
	}

	/** Code exécuté lors d'une erreur sur le get.
	 * @param poError Erreur reçue.
	 * @param psId Identifiant du fichier.
	 */
	private onGetError(poError: HttpErrorResponse, psId: string): Observable<never> {
		const lsErrorMessage = `Abandon du téléchargement du contenu du document ${psId}. ${poError.message}`;

		return (this.isPermanentError(poError) ? this.removeDownloadRequest(psId) : of(null))
			.pipe(
				tap(_ => {
					const loDmsEvent: IDmsEvent = {
						type: EApplicationEventType.DmsEvent,
						createDate: new Date(),
						data: { fileId: psId }
					};
					this.raiseDmsEvent(loDmsEvent);
				}),
				mergeMap(_ => throwError(lsErrorMessage))
			);
	}

	/** Demande le lancement des requêtes qui sont en attente (téléchargements, téléversements et suppressions). */
	public requestExecPendings(): void {
		this.raiseDmsEvent(this.createDmsEvent(EDmsAction.sync));
	}

	/** Demande le lancement d'une requête de téléchargement qui est en attente.
	 * @param psFileId Identifiant du fichier à télécharger.
	 */
	public requestExecPendingDownload(psFileId: string): void {
		this.raiseDmsEvent(this.createDmsEvent(EDmsAction.download, psFileId));
	}

	/** Demande le lancement des requêtes de téléversement qui sont en attente.
	 * @param psFileId Identifiant du fichier à téléverser.
	 */
	public requestExecPendingUpload(psFileId: string): void {
		this.raiseDmsEvent(this.createDmsEvent(EDmsAction.upload, psFileId));
	}

	/** Demande le lancement des requêtes de suppression qui sont en attente.
	 * @param psFileId Identifiant du fichier à supprimer.
	 */
	public requestExecPendingDelete(psFileId: string): void {
		this.raiseDmsEvent(this.createDmsEvent(EDmsAction.delete, psFileId));
	}

	/** Crée et retourne un objet événement dms.
	 * @param peAction Action souhaitée.
	 * @param psFileId Identifiant du fichier qu'on veut traiter.
	 */
	private createDmsEvent(peAction: EDmsAction, psFileId: string = ""): IDmsEvent {
		return {
			type: EApplicationEventType.DmsEvent,
			createDate: new Date(),
			data: {
				dmsAction: peAction,
				fileId: psFileId
			}
		};
	}

	/** Enregistre une requête de téléchargement dans la base de données locale du DMS.
	 * @param psGuid Guid du fichier à télécharger.
	 */
	private putDownloadRequest(psGuid: string): Observable<IStoreDataResponse> {
		return this.putRequest(psGuid, EPrefix.pendingDownload, "Erreur lors de l'enregistrement de la requête de téléchargement : ");
	}

	/** Enregistre une requête de suppression dans la base de données locale du DMS.
	 * @param psGuid Guid du fichier à supprimer.
	 */
	private putDeleteRequest(psGuid: string): Observable<IStoreDataResponse> {
		return this.putRequest(psGuid, EPrefix.pendingDelete, "Erreur lors de l'enregistrement de la requête de suppression : ");
	}

	/** Enregistre une requête de téléversement dans la base de données locale du DMS.
	 * @param psGuid Guid du fichier à téléverser.
	 */
	private putUploadRequest(psGuid: string): Observable<IStoreDataResponse> {
		return this.putRequest(psGuid, EPrefix.pendingUpload, "Erreur lors de l'enregistrement de la requête de téléversement : ");
	}

	/** Eregistre une requête dans la base de données locale du DMS.
	 * @param psGuid Guid du document à traiter.
	 * @param pePrefix Prefixe de la requête.
	 * @param psErrorMessage Message d'erreur.
	 */
	private putRequest(psGuid: string, pePrefix: EPrefix, psErrorMessage: string): Observable<IStoreDataResponse> {
		const lsId: string = IdHelper.buildId(pePrefix, GuidHelper.extractGuid(psGuid));

		return this.isvcStore.getOne({ databaseId: this.C_DMS_DATABASE_ID, viewParams: { key: lsId } }, false)
			.pipe(
				mergeMap((poResult?: IStoreDocument) => poResult ?
					of({ ok: true, id: poResult._id, rev: poResult._rev } as IStoreDataResponse) : this.isvcStore.put({ _id: lsId }, this.C_DMS_DATABASE_ID)
				),
				catchError(poError => { console.error(`${SyncDmsService.C_LOG_ID}${psErrorMessage}`, poError); return throwError(poError); })
			);
	}

	/** Supprime une requête de téléchargement de la base de données locale du DMS.
	 * @param psGuid Guid du document à supprimer.
	 */
	public removeDownloadRequest(psGuid: string): Observable<IStoreDataResponse> {
		return this.removeRequest(psGuid, EPrefix.pendingDownload, "Erreur lors de la suppression de la requete de téléchargement: ");
	}

	/** Supprime une requête de suppression de la base de données locale du DMS.
	 * @param psGuid Guid du document à supprimer.
	 */
	private removeDeleteRequest(psGuid: string): Observable<IStoreDataResponse> {
		return this.removeRequest(psGuid, EPrefix.pendingDelete, "Erreur lors de la suppression de la requete de suppression: ");
	}

	/** Supprime une requête de téléversement de la base de données locale du DMS.
	 * @param psGuid Guid du document à supprimer.
	 */
	private removeUploadRequest(psGuid: string): Observable<IStoreDataResponse> {
		return this.removeRequest(psGuid, EPrefix.pendingUpload, "Erreur lors de la suppression de la requete de téléversement: ")
			.pipe(tap(() => console.debug(`SDMS.S::Document ${psGuid} upload request removed.`)));
	}

	/** Supprime une requête de la base de données locale du DMS.
	 * @param psGuid Guid du document à traiter.
	 * @param pePrefix Prefixe de la requête.
	 * @param psErrorMessage Message d'erreur.
	 */
	private removeRequest(psGuid: string, pePrefix: EPrefix, psErrorMessage: string): Observable<IStoreDataResponse> {
		return this.isvcStore.delete(IdHelper.buildId(pePrefix, GuidHelper.extractGuid(psGuid)), this.C_DMS_DATABASE_ID)
			.pipe(catchError(poError => { console.error(`${SyncDmsService.C_LOG_ID}${psErrorMessage}`, poError); return throwError(poError); }));
	}

	/** Enregistre en local un fichier dans le DMS et tente un téléversement vers le serveur.
	 * @param poData Fichier à enregistrer dans le DMS et à téléverser.
	 * @param poMeta Méta du fichier à enregistrer.
	 */
	public save(poData: DmsFile, poMeta: IDmsMeta): Observable<IDmsMeta> {
		return this.isvcLocalDms.save(poData, poMeta)
			.pipe(
				mergeMap((poLocalResult: IDmsMeta) => this.putUploadRequest(poLocalResult._id).pipe(mapTo(poLocalResult))),
				tap((poResult: IDmsMeta) => this.requestExecPendingUpload(poResult._id))
			);
	}

	/** Permet de s'abonner aux événements du SyncDmsService.
	 * @param pfNext Fonction appelée lors du next => function(poResult: IDmsEvent).
	 * @param pfError Fonction appelée lors du error => function(poError: any).
	 * @param pfComplete Fonction appelée lors du complete.
	 */
	public subscribe(pfNext: Function, pfError?: Function, pfComplete?: Function): void {
		this.moDmsEventSubject.asObservable().subscribe(
			(poResult: IDmsEvent) => pfNext(poResult),
			poError => pfError(poError),
			() => pfComplete()
		);
	}

	/** Exécute tous les changements en attente enregistrés en base.
	 * @param pbHasToContinueIfFail `true` si on veut inclure les fichiers tombés en erreur (et permet de continuer les traitements),
	 * `false` sinon (`false` par défaut, tous les traitements s'arrêtent à la moindre erreur).
	 * @returns Le tableau des identifiants des fichiers traités (téléchargés, téléversés, supprimés), si `pbHasToReturnFailures` est à `true`,
	 * chaque fichier qui est tombé en erreur renverra la valeur `null`.
	 */
	public syncPendingChanges(pbHasToContinueIfFail: boolean = false): Observable<Array<string>> {
		console.debug(`${SyncDmsService.C_LOG_ID}Processing pending changes...`);

		return this.getPendingDocs(EPrefix.pending)
			.pipe(
				mergeMap((paResults: IStoreDocument[]) => this.execSync(paResults, pbHasToContinueIfFail)),
				mergeMap((paResults: string[]) => paResults.some((psId: string) => StringHelper.isBlank(psId)) ?
					throwError("Un ou plusieurs fichiers n'ont pu être synchronisés.") : of(paResults)
				),
				catchError(poError => {
					console.error(`${SyncDmsService.C_LOG_ID}Pending changes processing failed.`, poError);
					return throwError(poError);
				}),
				finalize(() => console.debug(`${SyncDmsService.C_LOG_ID}Pending changes processing completed.`))
			);
	}

	/** Exécute un téléchargement en attente.
	 * @param psFileGuid Guid du fichier à télécharger.
	 */
	public execPendingDownload(psFileGuid: string): Observable<Array<string>> {
		return this.getPendingDoc(EPrefix.pendingDownload, psFileGuid)
			.pipe(mergeMap((poResult: IStoreDocument) => this.execDownloads([poResult])));
	}

	/** Exécute un téléversement en attente.
	 * @param psFileGuid Guid du fichier à téléverser.
	 */
	public execPendingUpload(psFileGuid: string): Observable<Array<string>> {
		return this.getPendingDoc(EPrefix.pendingUpload, psFileGuid)
			.pipe(mergeMap((poResult: IStoreDocument) => this.execUploads([poResult])));
	}

	/** Exécute une suppression en attente.
	 * @param psFileGuid Guid du fichier à supprimer.
	 */
	public execPendingDelete(psFileGuid: string): Observable<Array<string>> {
		return this.getPendingDoc(EPrefix.pendingDelete, psFileGuid)
			.pipe(mergeMap((poResult: IStoreDocument) => this.execDeletes([poResult])));
	}

	/** Récupère un document en attente en fonction d'un préfixe et d'un guid de fichier.
	 * @param pePrefix Préfixe du document en attente à récupérer.
	 * @param psFileGuid Guid du fichier dont il faut récupérer le document en attente.
	 */
	private getPendingDoc(pePrefix: EPrefix, psFileGuid: string): Observable<IStoreDocument> {
		const loDataSource: IDataSource = {
			databaseId: ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.dmsStorage)),
			viewParams: {
				key: `${pePrefix}${GuidHelper.extractGuid(psFileGuid)}`,
				include_docs: true
			}
		};

		return this.isvcStore.getOne<IStoreDocument>(loDataSource, false).pipe(
			filter(poResult => !!poResult)
		);
	}

	/** Récupère les documents en attente en fonction d'un préfixe.
	 * @param pePrefix Préfixe des documents en attente à récupérer.
	 */
	public getPendingDocs(pePrefix: EPrefix, pbLive: boolean = false): Observable<IStoreDocument[]> {
		try {
			const loDataSource: IDataSource = {
				databaseId: ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.dmsStorage)),
				viewParams: {
					startkey: pePrefix,
					endkey: `${pePrefix}${Store.C_ANYTHING_CODE_ASCII}`,
					include_docs: true
				},
				live: pbLive,
			};

			return this.isvcStore.get(loDataSource);
		}
		catch {
			console.error(`${SyncDmsService.C_LOG_ID}Impossible de trouver les bases de données correspondant au rôle ${EDatabaseRole.dmsStorage}.`);
			return throwError(new Error("Pas d'emplacement de stockage pour les documents."));
		}
	}

	/** Permet d'observer les actions sur un fichier puis de renvoyer ses métas.
	 * @param psId Identifiant du fichier à observer.
	 */
	private waitFile(psId: string): Observable<IDmsData> {
		return DmsFileHelper.waitForFile(psId);
	}

	/** Récupère les fichiers en attente (delete/download/upload). */
	public getPendingFiles(): Observable<IStoreDocument[]> {
		return this.getPendingDocs(EPrefix.pending);
	}

	/** Récupère les documents en attente de téléchargement et téléversement.
	 * @param pbLive Indique si la récupération est continue, `false` par défaut.
	 */
	public getPendingDownloadAndUploadDocs(pbLive?: boolean): Observable<IStoreDocument[]> {
		return this.getPendingDocs(EPrefix.pending, pbLive)
			.pipe(map((paPendingDocs: IStoreDocument[]) => paPendingDocs.filter((poDoc: IStoreDocument) => !poDoc._id.includes(EPrefix.pendingDelete))));
	}

	//#endregion

}
